import { QueryFunction, useQuery } from 'react-query';
import {
    FormResponsesWithCountResponse,
    FormResponseWithDetails,
    FormResponseWithSiteAndFilledBy,
    FormResponseWithTemplate,
    getPendingSignaturesByUser,
    getResponseById,
    getResponsesByTemplateAndUserId,
    getResponsesByTemplateId,
} from '../../../../services/apiSLTP/qhse/forms/formResponses';

// ---------------- TYPES ----------------
type UseResponsesByTemplateAndUserQueryOptions = {
    templateId: string;
    userId: string;
};

type UseResponseByIdQueryOptions = {
    id: string;
};

type UseResponseByTemplateIdQueryOptions = {
    templateId: string;
};

type UsePendingSignaturesByUserQueryOptions = {
    userId: string;
};

// ---------------- QUERY FUNCTIONS ----------------

// Get responses by template & user
const getResponsesByTemplateAndUserQuery: QueryFunction<
    FormResponsesWithCountResponse,
    [string, UseResponsesByTemplateAndUserQueryOptions]
> = async ({ queryKey }) => {
    const [, { templateId, userId }] = queryKey;
    try {
        const response = await getResponsesByTemplateAndUserId(templateId, userId);
        return response;
    } catch (error) {
        throw new Error('An unexpected error occurred while fetching form responses');
    }
};

// Get response by id with details
const getResponseByIdQuery: QueryFunction<FormResponseWithDetails, [string, UseResponseByIdQueryOptions]> = async ({ queryKey }) => {
    const [, { id }] = queryKey;
    try {
        const response = await getResponseById(id);
        return response.data;
    } catch (error) {
        throw new Error('An unexpected error occurred while fetching the form response');
    }
};

const getResponseByTemplateIdQuery: QueryFunction<FormResponseWithSiteAndFilledBy[], [string, UseResponseByTemplateIdQueryOptions]> = async ({
    queryKey,
}) => {
    const [, { templateId }] = queryKey;
    try {
        const response = await getResponsesByTemplateId(templateId);
        return response.data;
    } catch (error) {
        throw new Error('An unexpected error occurred while fetching the form response');
    }
};

const getPendingSignaturesByUserQuery: QueryFunction<FormResponseWithTemplate[], [string, UsePendingSignaturesByUserQueryOptions]> = async ({
    queryKey,
}) => {
    const [, { userId }] = queryKey;
    try {
        const response = await getPendingSignaturesByUser(userId);
        return response.data;
    } catch (error) {
        throw new Error('An unexpected error occurred while fetching pending signatures');
    }
};

// ---------------- HOOKS ----------------

export const useResponsesByTemplateAndUserQuery = (options: UseResponsesByTemplateAndUserQueryOptions) => {
    return useQuery(['formResponsesByTemplateAndUser', options], getResponsesByTemplateAndUserQuery);
};

export const useResponseByIdQuery = (options: UseResponseByIdQueryOptions) => {
    return useQuery(['formResponseById', options], getResponseByIdQuery);
};

export const useResponseByTemplateIdQuery = (options: UseResponseByTemplateIdQueryOptions) => {
    return useQuery(['formResponseByTemplateId', options], getResponseByTemplateIdQuery);
};

export const usePendingSignaturesByUserQuery = (options: UsePendingSignaturesByUserQueryOptions) => {
    return useQuery(['pendingSignaturesByUser', options], getPendingSignaturesByUserQuery);
};
